declare const Sentry: any;
declare const UAParser: any;

type SeverityLevel = 'debug' | 'log' | 'info' | 'warning' | 'error' | 'fatal';

(function() { // wrap function scope to prevent pollution of the browser's global scope
// begin Sentry package internal code copy
// BH taken below directly from sentry-javascript/packages/utils/src/syncpromise.ts, v7.64

/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/typedef */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
function isThenable(wat: any): wat is PromiseLike<any> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  return Boolean(wat && wat.then && typeof wat.then === 'function');
}

/** SyncPromise internal states */
const enum States {
  /** Pending */
  PENDING = 0,
  /** Resolved / OK */
  RESOLVED = 1,
  /** Rejected / Error */
  REJECTED = 2,
}

const mapSentrySeverityToCoralogix: { [key in SeverityLevel]: string } = {
  fatal: 'critical',
  error: 'error',
  warning: 'warn',
  log: 'verbose',
  info: 'info',
  debug: 'debug',
};

const mapCoralogixToConsole: { [key in 'critical' | 'error' | 'warn' | 'verbose' | 'info' | 'debug' ]: string } = {
  critical: 'error',
  error: 'error',
  warn: 'warn',
  verbose: 'log',
  info: 'info',
  debug: 'debug',
};

// Overloads so we can call resolvedSyncPromise without arguments and generic argument
function resolvedSyncPromise(): PromiseLike<void>;
function resolvedSyncPromise<T>(value: T | PromiseLike<T>): PromiseLike<T>;

/**
 * Creates a resolved sync promise.
 *
 * @param value the value to resolve the promise with
 * @returns the resolved sync promise
 */
function resolvedSyncPromise<T>(value?: T | PromiseLike<T>): PromiseLike<T> {
  return new SyncPromise(resolve => {
    resolve(value);
  });
}

/**
 * Creates a rejected sync promise.
 *
 * @param value the value to reject the promise with
 * @returns the rejected sync promise
 */
function rejectedSyncPromise<T = never>(reason?: any): PromiseLike<T> {
  return new SyncPromise((_, reject) => {
    reject(reason);
  });
}

/**
 * Thenable class that behaves like a Promise and follows it's interface
 * but is not async internally
 */
class SyncPromise<T> implements PromiseLike<T> {
  private _state: States;
  private _handlers: Array<[boolean, (value: T) => void, (reason: any) => any]>;
  private _value: any;

  public constructor(
    executor: (resolve: (value?: T | PromiseLike<T> | null) => void, reject: (reason?: any) => void) => void,
  ) {
    this._state = States.PENDING;
    this._handlers = [];

    try {
      executor(this._resolve, this._reject);
    } catch (e) {
      this._reject(e);
    }
  }

  /** JSDoc */
  public then<TResult1 = T, TResult2 = never>(
    onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return new SyncPromise((resolve, reject) => {
      this._handlers.push([
        false,
        result => {
          if (!onfulfilled) {
            resolve(result as any);
          } else {
            try {
              resolve(onfulfilled(result));
            } catch (e) {
              reject(e);
            }
          }
        },
        reason => {
          if (!onrejected) {
            reject(reason);
          } else {
            try {
              resolve(onrejected(reason));
            } catch (e) {
              reject(e);
            }
          }
        },
      ]);
      this._executeHandlers();
    });
  }

  /** JSDoc */
  public catch<TResult = never>(
    onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null,
  ): PromiseLike<T | TResult> {
    return this.then(val => val, onrejected);
  }

  /** JSDoc */
  public finally<TResult>(onfinally?: (() => void) | null): PromiseLike<TResult> {
    return new SyncPromise<TResult>((resolve, reject) => {
      let val: TResult | any;
      let isRejected: boolean;

      return this.then(
        value => {
          isRejected = false;
          val = value;
          if (onfinally) {
            onfinally();
          }
        },
        reason => {
          isRejected = true;
          val = reason;
          if (onfinally) {
            onfinally();
          }
        },
      ).then(() => {
        if (isRejected) {
          reject(val);
          return;
        }

        resolve(val as unknown as any);
      });
    });
  }

  /** JSDoc */
  private readonly _resolve = (value?: T | PromiseLike<T> | null) => {
    this._setResult(States.RESOLVED, value);
  };

  /** JSDoc */
  private readonly _reject = (reason?: any) => {
    this._setResult(States.REJECTED, reason);
  };

  /** JSDoc */
  private readonly _setResult = (state: States, value?: T | PromiseLike<T> | any) => {
    if (this._state !== States.PENDING) {
      return;
    }

    if (isThenable(value)) {
      void (value as PromiseLike<T>).then(this._resolve, this._reject);
      return;
    }

    this._state = state;
    this._value = value;

    this._executeHandlers();
  };

  /** JSDoc */
  private readonly _executeHandlers = () => {
    if (this._state === States.PENDING) {
      return;
    }

    const cachedHandlers = this._handlers.slice();
    this._handlers = [];

    cachedHandlers.forEach(handler => {
      if (handler[0]) {
        return;
      }

      if (this._state === States.RESOLVED) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        handler[1](this._value as unknown as any);
      }

      if (this._state === States.REJECTED) {
        handler[2](this._value);
      }

      handler[0] = true;
    });
  };
}

// BH taken below directly from sentry-javascript/packages/browser/src/transports/xhr.ts, v7.64
// BH custom code sections marked where needed, from '@classes/Sentry'

/**
 * Create a Transport that uses the console to send events for local
 */
function makeConsoleTransport(
  options,
) {
  function makeRequest(request) {
    const data = [];
    if (typeof request.body === 'string') {
      const dataSet = request.body.split('\n');
      dataSet.forEach((value) => {
        data.push(JSON.parse(value));
      });
    }

    return new Promise((resolve, reject) => {
      const error = data.filter((obj) => !!obj.tags && !!obj.message)[0];

      console[mapCoralogixToConsole[error.tags.severity]](error.message, data);
      resolve({
        statusCode: 200,
      });
    });
  }

  return Sentry.createTransport(options, makeRequest);
}

/**
 * The DONE ready state for XmlHttpRequest
 *
 * Defining it here as a constant b/c XMLHttpRequest.DONE is not always defined
 * (e.g. during testing, it is `undefined`)
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState}
 */
const XHR_READYSTATE_DONE = 4;

/**
 * Creates a Transport that uses the XMLHttpRequest API to send events to Sentry.
 */
function makeXHRTransport(options) {
  function makeRequest(request) {
    return new SyncPromise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.onerror = reject;

      xhr.onreadystatechange = (): void => {
        if (xhr.readyState === XHR_READYSTATE_DONE) {
          resolve({
            statusCode: xhr.status,
            headers: {}, // BH removed unnecessary Sentry API response headers, which error out
          });
        }
      };

      xhr.open('POST', options.url);

      for (const header in options.headers) {
        if (Object.prototype.hasOwnProperty.call(options.headers, header)) {
          xhr.setRequestHeader(header, options.headers[header])
        }
      }

      // begin BH custom code
      if (typeof request.body === 'string') {
        // Sentry payload shows invalid JSON: multiple blocks instead of one. Wrap it in a valid JSON array instead for Coralogix.

        // reference sentry-javascript/packages/utils/src/envelope.ts::serializeEnvelope()
        // where an item is itemHeaders+payload, and that can repeat
        let i = 0;
        // Sentry payload shows invalid JSON: multiple blocks instead of one. Wrap it in a valid JSON array instead for Coralogix.
        request.body = `{"envelope": {"envHeaders": ${request.body.replace(/}\n{/g, () => {
          let key = (i % 2) ? 'payload' : 'itemHeaders';
          if (i >= 2) {
            const itemIndex = parseInt(String(i / 2), 10);
            key += `_${itemIndex}`;
          }
          i += 1;
          return `}, "${key}": {`;
        })}}}`;
      }

      request.body = JSON.stringify({
        privateKey: options.privateKey,
        applicationName: options.applicationName,
        subsystemName: options.subsystemName,
        computerName: options.computerName,
        logEntries: [{
          timestamp: (new Date()).getTime(),
          severity: 5, // error
          text: request.body,
        }],
      });
      // end BH custom code

      xhr.send(request.body)
    })
  }

  return Sentry.createTransport(options, makeRequest)
}

// end Sentry package internal code copy

// Found in Manage --> Settings
const settings = {
  msfrontend_logger_ignore_regex_useragent: page_info.msfrontend_logger_ignore_regex_useragent,
  msfrontend_logger_ignore_regex_eventmessage: page_info.msfrontend_logger_ignore_regex_eventmessage,
};

// copied from '@classes/Sentry' beforeSend_base()
let uaContext;
let browser;

function beforeSend_base(event: any, hint: any) {
  // The following wasn't possible to set up within a Coralogix rule due to the resulting 'values' field being an array
  if (!('message' in event)) event.message = hint?.originalException?.message || event.exception?.values?.[0]?.value;

  if ((settings.msfrontend_logger_ignore_regex_useragent && (new RegExp(settings.msfrontend_logger_ignore_regex_useragent)).test(event.request.headers['User-Agent']))
    || (settings.msfrontend_logger_ignore_regex_eventmessage && (new RegExp(settings.msfrontend_logger_ignore_regex_eventmessage)).test(event.message))) {
    return null;
  }

  if (event.exception?.values?.[0]?.mechanism.type === 'http.client') {
    event.tags.statusCode = event.contexts.response.status_code;
    if ([401, 403].indexOf(event.tags.statusCode) !== -1) {
      event.tags.severity = 'warning';
    }
  }

  event.exception?.values.forEach((item, index) => {
    if (index > 0) event[`message_${index}`] = item.value;
  });

  // Clean out "unnecessary" extras, to save on Coralogix data storage quota and stop truncation of the payload due to request size.
  delete hint?.originalException?.stack;
  if (Array.isArray(event.exception?.values)) {
    event.exception.values.forEach((item) => {
      if (item.stacktrace?.frames) {
        item.stacktrace.frames = item.stacktrace.frames.slice(-3);
      }
    });
  }

  delete event.contexts;
  event.contexts = {};

  if (!uaContext) { // only need UA one time per browser session
    try {
      uaContext = UAParser(event.request.headers['User-Agent']);
      delete uaContext.ua; // remove 'ua' field as it is redundant with 'User-Agent' field already

      if (uaContext.browser.name) {
        browser = uaContext.browser.name;

        if (uaContext.browser.major) {
          browser += ` ${uaContext.browser.major}`;
        }
      } else {
        browser = 'unknown';
      }
    } catch (e) {
      browser = 'unknown';
    }
  }

  event.contexts.UAParser = uaContext;
  event.browser = browser;

  return event;
}

function makeInitOptions({ platform, coralogixApiUrl, coralogixApiKey, userId, versions, isDevelopment, stagingServerName }) {
  let environment = 'prod';
  let applicationName = 'msfrontend';
  let subsystemName = 'runtime';

  if (isDevelopment || stagingServerName) {
    applicationName += '-development'; // update this to something more accurate - maybe '-nonprod'

    if (stagingServerName) {
      environment = 'development';
      subsystemName += `-${stagingServerName}`;
    } else {
      environment = 'local';
      subsystemName += '-local';
    }
  }

  const tags = { platform, severity: 'error' };
  Object.keys(versions).forEach((key) => {
    tags[`version_${key}`] = versions[key];
  });

  const initOptions = {
    dsn: 'https://api@coralogix/1', // unused
    tunnel: coralogixApiUrl,
    environment,
    transport: page_info.is_development_local ? makeConsoleTransport : makeXHRTransport,
    transportOptions: {
      headers: {
        'Content-Type': 'application/json',
      },
      privateKey: coralogixApiKey,
      computerName: 'msfrontend',
      applicationName,
      subsystemName,
    },
    sendClientReports: false,
    initialScope: {
      tags,
      user: { id: userId },
    },
    tracesSampler: () => 0,
    beforeBreadcrumb: () => null,
    beforeSendTransaction: () => null,

    beforeSend: (event: any, hint: any) => {
      // Default set severity to error unless set in scope
      event.tags.severity = mapSentrySeverityToCoralogix[hint?.captureContext] || 'error';

      return beforeSend_base(event, hint);
    },
  };

  return initOptions;
}

if (window.Sentry) {
  const versions = {
    app: page_info.app_version,
    assets: page_info.assets_app_version,
    contrib: page_info.contrib_app_version,
    image: page_info.env_image_tag,
  };

  const options = makeInitOptions({
    platform: 'php-clientside',
    coralogixApiUrl: page_info.coralogix_api_url,
    coralogixApiKey: page_info.coralogix_api_key,
    userId: page_info.track_userid,
    versions,
    isDevelopment: false,
    stagingServerName: page_info.is_qa_server ? page_info.subdomain : null,
  });

  const integrations = (_integrations) => {
    const namesToRemove = ['Breadcrumbs'];
    const integrations = _integrations.filter((integration) => namesToRemove.indexOf(integration.name) === -1);

    if (!page_info.is_development_local) {
      integrations.push(new Sentry.Integrations.CaptureConsole({
        levels: ['error'],
      }));
    }
    integrations.push(new Sentry.Integrations.HttpClient({
      failedRequestStatusCodes: [401, 403, 404, [500, 599]],
    }));

    return integrations;
  };

  Sentry.init({ ...options, integrations });
}
})();

/**
 * polyfill
 *
 * Note: find a better place for this.
 */
if (!Promise.allSettled) {
  Promise.allSettled = (promises) => Promise.all(
    promises.map((p) => p
      .then((value) => ({
        status: 'fulfilled',
        value,
      }))
      .catch((reason) => ({
        status: 'rejected',
        reason,
      }))),
  );
}

function track(message: Error | string, severity: SeverityLevel = 'error'): string {
  if (typeof message === 'string') {
    return Sentry.captureException(new Error(message), severity);
  }

  return Sentry.captureException(message, severity);
}

function debug(error: Error | string): string {
  return track(error, 'debug');
}

function info(error: Error | string): string {
  return track(error, 'info');
}

function warning(error: Error | string): string {
  return track(error, 'warning');
}

function error(error: Error | string): string {
  return track(error, 'error');
}

/**
 * DEPRECATED Use `logger` instead
 * This is only intended for backwards compatability with current track calls
 */
window.trackJs = {
  track,
}

window.logger = {
  track,
  debug,
  info,
  warning,
  error,
}
